import React from "react";
import Popup from "reactjs-popup";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslate } from "../../../../components/TranslationText";
import { formatDateTimeToString } from "../../../../helper/formatDateTimeToString";
import DetailField from "../../../../components/DetailField";
import CloseButton from "../../../../components/CloseButton";
import TranslationsKeys from "../../../../TranslationsKeys";

const getDocumentFileName = (url = "") => {
    if (!url) return "";
    const cleanUrl = url.split("?")[0].split("#")[0];
    return cleanUrl.split("/").filter(Boolean).pop() || url;
};

const PassportDeliveryPopup = ({ open, onClose, deliveryItem }) => {
    const { translate } = useTranslate();
    const { currentLanguage } = useSelector((state) => state.studBookSlice);

    const handleCopyShipmentNumber = (num) => {
        if (!num) return;
        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(String(num));
        } else {
            const textarea = document.createElement("textarea");
            textarea.value = String(num);
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
        }
        toast.success(translate(TranslationsKeys.COPIED_SUCCESSFULLY, "Copied to clipboard!"));
    };

    const handleOpenShipmentDocument = (doc) => {
        if (!doc) return;
        if (
            doc.startsWith("http://") ||
            doc.startsWith("https://") ||
            doc.startsWith("blob:") ||
            doc.startsWith("data:")
        ) {
            window.open(doc, "_blank");
            return;
        }
        const baseUrl = (process.env.REACT_APP_UMBRACO || "https://site.eahs.ae:8016").replace(/\/+$/, "");
        const fullUrl = doc.startsWith("/") ? `${baseUrl}${doc}` : `${baseUrl}/media/${doc}`;
        window.open(fullUrl, "_blank");
    };

    const serviceType = deliveryItem?.serviceDefinitionName?.trim() || "";
    const refNo = deliveryItem?.referenceNumber?.trim() || "";
    const deliveryStatus = deliveryItem?.deliveryStatusName?.trim() || "";
    const shipmentNumber = deliveryItem?.shipmentNumber?.trim() || "";
    const shipmentDocument = deliveryItem?.shipmentDocument?.trim() || "";

    const shippedDate =
        deliveryItem?.shippedDateFormatted ||
        (deliveryItem?.shippedDate ? formatDateTimeToString(deliveryItem.shippedDate) : "");

    const deliveryReturnDate =
        deliveryItem?.deliveryReturnDateFormatted ||
        (deliveryItem?.deliveryReturnDate ? formatDateTimeToString(deliveryItem.deliveryReturnDate) : "");

    const passportRecipientArabicName = deliveryItem?.passportRecipientArabicName?.trim() || "";
    const passportRecipientEnglishName = deliveryItem?.passportRecipientEnglishName?.trim() || "";

    const returnNote = deliveryItem?.returnNote?.trim() || "";

    const hasAnyData =
        !!serviceType ||
        !!refNo ||
        !!deliveryStatus ||
        !!shipmentNumber ||
        !!shipmentDocument ||
        !!shippedDate ||
        !!deliveryReturnDate ||
        !!passportRecipientArabicName ||
        !!passportRecipientEnglishName ||
        !!returnNote;

    return (
        <Popup
            open={open}
            onClose={onClose}
            modal
            closeOnEscape={true}
            closeOnDocumentClick={true}
            lockScroll={true}
            overlayStyle={{ zIndex: 5000, backgroundColor: "rgba(0,0,0,0.4)" }}
            contentStyle={{
                width: "92%",
                maxWidth: "480px",
                padding: "24px",
                borderRadius: "16px",
                backgroundColor: "#fff",
                border: "none",
                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
            }}
        >
            {(close) => (
                <div
                    tabIndex={-1}
                    style={{
                        direction: currentLanguage === "ar" ? "rtl" : "ltr",
                        textAlign: currentLanguage === "ar" ? "right" : "left",
                        outline: "none",
                    }}
                >
                    {/* Header */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4
                            className="fw-bold m-0"
                            style={{ fontSize: "20px", color: "#111" }}
                        >
                            {translate(TranslationsKeys.PASSPORT_DELIVERY_DETAILS, "Passport Delivery Details")}
                        </h4>
                        <CloseButton onClick={close} />
                    </div>

                    {/* Service info */}
                    {(!!serviceType || !!refNo) && (
                        <div className="mb-3" style={{ fontSize: "15px", lineHeight: "1.5" }}>
                            {!!serviceType && (
                                <div className="mb-2">
                                    <span className="fw-bold" style={{ color: "#111" }}>
                                        {translate(TranslationsKeys.SERVICE_TYPE, "Service Type")}:{" "}
                                    </span>
                                    <span style={{ color: "#333" }}>{serviceType}</span>
                                </div>
                            )}
                            {!!refNo && (
                                <div>
                                    <span className="fw-bold" style={{ color: "#111" }}>
                                        {translate(TranslationsKeys.REF_NO, "Ref.No")}:{" "}
                                    </span>
                                    <span style={{ color: "#333" }}>{refNo}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Delivery Status */}
                    <DetailField
                        label={translate(TranslationsKeys.DELIVERY_STATUS, "Delivery Status")}
                        value={deliveryStatus}
                    />

                    {/* Shipment Number */}
                    <DetailField
                        label={translate(TranslationsKeys.SHIPMENT_NUMBER, "Shipment Number")}
                        value={shipmentNumber}
                        dir="ltr"
                        action={
                            <button
                                type="button"
                                onClick={() => handleCopyShipmentNumber(shipmentNumber)}
                                tabIndex={-1}
                                className="btn btn-link p-0 border-0 shadow-none"
                                style={{
                                    color: "#C89C66",
                                    fontSize: "18px",
                                    lineHeight: 1,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    outline: "none",
                                    boxShadow: "none",
                                }}
                                title={translate(TranslationsKeys.COPY, "Copy")}
                            >
                                <i className="fa-regular fa-copy"></i>
                            </button>
                        }
                    />

                    {/* Shipment Document */}
                    <DetailField
                        label={translate(TranslationsKeys.SHIPMENT_DOCUMENT, "Shipment Document")}
                        value={getDocumentFileName(shipmentDocument)}
                        dir="ltr"
                        action={
                            <button
                                type="button"
                                onClick={() => handleOpenShipmentDocument(shipmentDocument)}
                                tabIndex={-1}
                                className="btn btn-link p-0 border-0 flex-shrink-0 shadow-none"
                                style={{
                                    color: "#C89C66",
                                    fontSize: "18px",
                                    lineHeight: 1,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    outline: "none",
                                    boxShadow: "none",
                                }}
                                title={translate(TranslationsKeys.VIEW, "View")}
                            >
                                <i className="fa-regular fa-eye"></i>
                            </button>
                        }
                    />

                    {/* Shipped Date: uses page direction so Arabic dates like "15 أكتوبر 2026 1:58م" flow correctly */}
                    <DetailField
                        label={translate(TranslationsKeys.SHIPPED_DATE, "Shipped Date")}
                        value={shippedDate}
                        dir={currentLanguage === "ar" ? "rtl" : "ltr"}
                    />

                    {/* Delivery Return Date: uses page direction */}
                    <DetailField
                        label={translate(TranslationsKeys.DELIVERY_RETURN_DATE_TO_EAHS_OFFICE, "Delivery Return Date To EAHs Office")}
                        value={deliveryReturnDate}
                        dir={currentLanguage === "ar" ? "rtl" : "ltr"}
                    />

                    {/* Passport Recipient Arabic Name */}
                    <DetailField
                        label={translate(TranslationsKeys.PASSPORT_RECIPIENT_ARABIC_NAME, "Passport Recipient Arabic Name")}
                        value={passportRecipientArabicName}
                        dir="rtl"
                    />

                    {/* Passport Recipient English Name */}
                    <DetailField
                        label={translate(TranslationsKeys.PASSPORT_RECIPIENT_ENGLISH_NAME, "Passport Recipient English Name")}
                        value={passportRecipientEnglishName}
                        dir="ltr"
                    />

                    {/* Return Note */}
                    <DetailField
                        label={translate(TranslationsKeys.RETURN_NOTE, "Return Note")}
                        value={returnNote}
                        isMultiLine={true}
                    />

                    {!hasAnyData && (
                        <div className="text-center text-muted py-4" style={{ fontSize: "14px" }}>
                            {translate(TranslationsKeys.NO_DELIVERY_DETAILS_AVAILABLE, "No delivery details available.")}
                        </div>
                    )}
                </div>
            )}
        </Popup>
    );
};

export default PassportDeliveryPopup;
